/**
 * LookupHighlightLayer — Invisible mirror of the textarea content that paints a
 * highlight pill behind each confirmed entity range.
 *
 * The textarea content carries *only* the entity name for confirmed picks (no
 * leading `/`), so this layer no longer scans the value for `/EntityName`
 * substrings. Instead the caller maintains an explicit `LookupInputRange[]`
 * (committed when the user picks a suggestion, rebased on every edit) and
 * passes it down via `ranges`. The pill simply colours the [start, end) span
 * of the value.
 *
 * Layout contract (must hold for the highlight to line up pixel-perfectly):
 *   • The layer is rendered inside a `position: relative` wrapper that also
 *     contains the textarea, with `position: absolute; inset: 0; pointer-events:
 *     none` so it stretches to fill the wrapper *behind* the textarea.
 *   • The layer's font, line-height, padding, and wrap classes must EXACTLY
 *     match the textarea's. Callers pass `mirrorClassName` to share typography
 *     classes with the textarea (see `AgenticSearchInput`).
 *   • A trailing zero-width space is appended when the value ends with `\n`
 *     so an empty trailing line doesn't get collapsed by the renderer.
 *
 * The layer also subscribes to `textareaRef`'s scroll so highlight pills stay
 * glued to their characters when the textarea overflows internally.
 */

import React, { useEffect, useRef } from 'react';
import type { LookupInputRange } from './lookupInputTokens';

export interface LookupHighlightLayerProps {
    value: string;
    /** Confirmed entity ranges in `value`. Must be in left-to-right order and
     *  non-overlapping; positions are inclusive/exclusive `[start, end)`. */
    ranges: LookupInputRange[];
    /** Ref to the textarea this layer is mirroring (used for scroll-sync). */
    textareaRef: React.RefObject<HTMLTextAreaElement | null>;
    /** Typography/box-model classes that MUST match the textarea exactly. */
    mirrorClassName: string;
    'data-test-id'?: string;
}

interface RenderSegment {
    type: 'text' | 'entity';
    text: string;
}

function buildSegments(value: string, ranges: LookupInputRange[]): RenderSegment[] {
    if (ranges.length === 0) return [{ type: 'text', text: value }];
    // Defensive: sort ranges by start in case the caller passed them unordered.
    const sorted = [...ranges].sort((a, b) => a.start - b.start);
    const segments: RenderSegment[] = [];
    let cursor = 0;
    for (const r of sorted) {
        // Clamp against `value.length` so a stale range (briefly out of sync
        // during a controlled-value handoff) doesn't throw or duplicate text.
        const start = Math.max(cursor, Math.min(r.start, value.length));
        const end = Math.max(start, Math.min(r.end, value.length));
        if (start > cursor) {
            segments.push({ type: 'text', text: value.slice(cursor, start) });
        }
        if (end > start) {
            segments.push({ type: 'entity', text: value.slice(start, end) });
        }
        cursor = end;
    }
    if (cursor < value.length) {
        segments.push({ type: 'text', text: value.slice(cursor) });
    }
    return segments;
}

const LookupHighlightLayer: React.FC<LookupHighlightLayerProps> = ({
    value,
    ranges,
    textareaRef,
    mirrorClassName,
    'data-test-id': dataTestId,
}) => {
    const mirrorRef = useRef<HTMLDivElement | null>(null);

    // Keep the mirror's scroll position aligned with the textarea so the
    // highlight pills track the visible text when the textarea overflows.
    useEffect(() => {
        const ta = textareaRef.current;
        const mirror = mirrorRef.current;
        if (!ta || !mirror) return;
        const sync = () => {
            mirror.scrollTop = ta.scrollTop;
            mirror.scrollLeft = ta.scrollLeft;
        };
        sync();
        ta.addEventListener('scroll', sync, { passive: true });
        return () => ta.removeEventListener('scroll', sync);
    }, [textareaRef, value]);

    const segments = buildSegments(value, ranges);

    return (
        <div
            ref={mirrorRef}
            aria-hidden="true"
            data-lookup-highlight-layer
            data-test-id={dataTestId}
            // Absolute fill of the relative wrapper; never blocks pointer events
            // on the textarea above. `overflow: hidden` clips text that the
            // textarea has scrolled out of view.
            className={`absolute inset-0 pointer-events-none overflow-hidden whitespace-pre-wrap break-words ${mirrorClassName}`}
            // `color: transparent` keeps the layer invisible — only the entity
            // pill backgrounds paint pixels. The textarea on top renders the
            // actual readable glyphs (entity names + surrounding free text).
            style={{ color: 'transparent' }}
        >
            {segments.map((seg, i) =>
                seg.type === 'entity' ? (
                    <span
                        key={i}
                        className="bg-[#EFE6FA] rounded-[6px]"
                        // The pill background needs the same line metrics as the
                        // surrounding text. Using `box-decoration-break: clone`
                        // keeps wrapped tokens (e.g. `Coca Cola` across lines)
                        // looking like one pill per line.
                        style={{ boxDecorationBreak: 'clone', WebkitBoxDecorationBreak: 'clone' }}
                    >
                        {seg.text}
                    </span>
                ) : (
                    <React.Fragment key={i}>{seg.text}</React.Fragment>
                ),
            )}
            {/* Zero-width trailing char prevents the final empty line from
                collapsing when `value` ends with `\n`. */}
            {value.endsWith('\n') && '\u200B'}
        </div>
    );
};

export default LookupHighlightLayer;
